import { Autocomplete, AutocompleteChangeReason, AutocompleteCloseReason, AutocompleteInputChangeReason, Avatar, Box, ClickAwayListener, Grid, List, Paper, Stack, TextField, Typography } from '@mui/material';
import { AccessKeys, GLOBAL_SEARCH_DELAY_DEBOUNCE } from 'app/appConstants';
import { AppImageDimension, AppImages } from "app/appImages";
import { MprcChipChoice, MprcClickable, MprcLink } from "components";
import { IconAI } from 'components/icons';
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { MprcSpinnerIndicator } from "components/MprcSpinnerIndicator";
import { CreateEnrollResults, EnrollStatus, GlobalSearchPreviewDto, TrainingSearchDto } from 'generated/CoreApi_v1';
import { useSimpleDebounce } from 'hooks/useDebounce';
import { Formaters } from "lib/Formaters";
import { useCoreApi } from 'providers/CoreApiProvider';
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { LmsPaths } from "routes/LmsRouter";
import { RootPaths } from 'routes/RootRouter';
import { NaturalLanguageQueryType, NaturalLanguageTypeDetector } from 'services/naturalLanguageDetector';
import { resolveLmsTargetAction } from "views/general/Cards/CardDtoActions";
import { ICardDtoPathResult } from "views/general/Cards/Common";

////////////////////////////////////////////////////////////////////////////

const MinChars = 2;

export const GlobalSearchFirstChars = "@#*~";

export type SearchTypeOption = "simple" | "ia" | "user" | "topic" | "channel" | "training";

interface SearchType {
  id: SearchTypeOption;
  label: string;
  placeholder?: string;
  hasSeparator?: boolean;
  charSearch?: string;
}

////////////////////////////////////////////////////////////////////////////

export const HeaderSearch: React.FC = () => {

  const api = useCoreApi();
  const navigate = useNavigate();
  const { t } = useTranslation("shared");

  const [searchParams, setSearchParams] = useSearchParams();

  const [opened, setOpened] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [globalSearchText, setGlobalSearchText] = useState<string>("");
  const [inputText, setInputText] = useState<string>("");
  const [focused, setFocused] = useState(false);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [selectedType, setSelectedType] = useState<SearchType | null>(null);
  const [searchResult, setSearchResult] = useState<GlobalSearchPreviewDto | null>(null);
  const [dialog, setDialog] = useState<any>(null);

  const searchRef = useRef<HTMLInputElement>(null);
  const lastSearchType = useRef<SearchType | null>(null);

  const keepSearchingPartialResults = useRef<boolean>(true);
  const lastSearchWithoutResults = useRef<string | null>(null);
  const [lastSearch, setLastSearch] = useState<string | null>(null);
  const debouncedSearchText = useSimpleDebounce<string>(globalSearchText, GLOBAL_SEARCH_DELAY_DEBOUNCE);

  const globalSearchPaths = useMemo(() => [
    RootPaths.FullGlobalSearch,
    RootPaths.FullChannelSearch,
    RootPaths.FullUserSearch,
    RootPaths.FullTopicSearch,
    RootPaths.FullTraining
  ], []);

  const searchTypes: SearchType[] = useMemo(() => [
    { id: 'simple', label: t("GlobalSearch.Types.Simple") },
    { id: 'ia', label: t("GlobalSearch.Types.IA"), placeholder: t("GlobalSearch.Types.IAPlaceholder"), hasSeparator: true },
    { id: 'user', label: t("GlobalSearch.Types.User"), placeholder: t("GlobalSearch.Types.UserPlaceholder"), charSearch: "@" },
    { id: 'topic', label: t("GlobalSearch.Types.Topic"), charSearch: "#" },
    { id: 'channel', label: t("GlobalSearch.Types.Channel"), charSearch: "*" },
    { id: 'training', label: t("GlobalSearch.Types.Training"), charSearch: "~" }
  ], []);

  //////////////////////////////////////////////////

  ///
  const clearSearchParams = () => {
    if (searchParams.has("q") &&
      globalSearchPaths.every(value => !document.location.pathname.includes(value))) {
      setSearchParams((prevSearchParams) => {
        prevSearchParams.delete("q");
        prevSearchParams.delete("st");
        return prevSearchParams;
      }, { replace: true });
    }
  };

  ///
  const reset = () => {
    setFocused(false);
    setLoading(false);
    setOpened(false);
    setGlobalSearchText("");
    setSearchResult(null);
  };

  ///
  const resetInput = () => {
    reset();
    setInputText("");
  };

  const searchTextChange = () => {
    var text = searchRef?.current?.value ?? "";
    setGlobalSearchText(text);
    setInputText(text);

    if (text.length < MinChars) {
      setOpened(false);
    }
  };

  ///
  const handleFocus = () => {
    setFocused(true);
    searchTextChange();
  };

  ///
  const handleBlur = () => {
    setFocused(false);
    if (!selectedType) {
      setShowTypeSelector(false);
    }
  };

  ///
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Se usuário pressionar BACKSPACE quando estiver no início do texto que está digitando: remove tipo de busca selecionado
    if (event.key === 'Backspace' && inputText === '' && selectedType) {
      event.preventDefault();
      clearSearchType();
    }

    //Se pressionar ENTER
    if (event.key == "Enter") {
      if (globalSearchText.length >= MinChars) {
        redirect();
      }
    }

    if (event.key == " " && inputText.trim().length > 0) {
      load();
    }
  };

  ///
  const handleInputChange = (
    e: any,
    newInputText: string,
    reason: AutocompleteInputChangeReason
  ) => {
    // Se o usuário digitou @ como primeiro caractere
    if (newInputText === '@' && !selectedType) {
      setShowTypeSelector(true);
      setInputText("@");
      return;
    }

    //Bloqueia digitação enquanto esta com opções para seleção até que usuário apague o "@"
    if (showTypeSelector) {
      if (newInputText === '') setShowTypeSelector(false);
      else return;
    }

    // Atualiza o valor normalmente
    searchTextChange();
  };


  ///
  const handleClose = () => {
    if (!focused) {
      reset();
    }
  };

  const getPlaceholder = () => {
    if (!focused) {
      return t("GlobalSearch.Placeholder");
    }
    if (focused && !selectedType) {
      if (showTypeSelector)
        return t("GlobalSearch.SelectTypePlaceholder");

      return t("GlobalSearch.FocusPlaceholder");
    }
    return selectedType?.placeholder ?? t("GlobalSearch.Types.CommonPlaceholder");
  };

  ///
  const goAiMode = () => {
    navigate(RootPaths.RagSearch, { replace: true });
  };

  ///
  const redirect = async () => {
    const q = "?q=" + encodeURIComponent(globalSearchText);
    var currentSelectedType = selectedType?.id;
    if (currentSelectedType == null) {
      const type = NaturalLanguageTypeDetector.detectQueryType(globalSearchText);
      currentSelectedType = type == NaturalLanguageQueryType.NaturalLanguageQuestion ? "ia" : "simple";
    }

    if (currentSelectedType == "simple")
      navigate(RootPaths.FullGlobalSearch + q + (selectedType != null ? `&st=${selectedType?.id}` : ""), { replace: document.location.pathname.includes(RootPaths.FullGlobalSearch) });
    if (currentSelectedType == "ia") {
      resetInput();
      navigate(RootPaths.RagSearch, { state: { searchText: globalSearchText } });
    }
    if (currentSelectedType == "user")
      navigate(RootPaths.FullUserSearch + q + `&st=${currentSelectedType}`);
    if (currentSelectedType == "topic")
      navigate(RootPaths.FullTopicSearch + q + `&st=${currentSelectedType}`);
    if (currentSelectedType == "channel")
      navigate(RootPaths.FullChannelSearch + q + `&st=${currentSelectedType}`);
    if (currentSelectedType == "training")
      navigate(RootPaths.FullTraining + q + `&st=${currentSelectedType}`);

    reset();
  };

  ///
  const goUrl = (url: string) => {
    handleClose();
    setSearchParams({ q: encodeURIComponent(globalSearchText), st: lastSearchType.current?.id ?? "" }, { replace: true });
    navigate(url);
  };

  const selectSearchType = (type: SearchType) => {
    setSelectedType(type);
    setShowTypeSelector(false);
    if (inputText != '') {
      setInputText('');
      setGlobalSearchText('');
    }

    // Retorna o foco para o input
    setTimeout(() => {
      searchRef.current?.focus();
    }, 100);

  };

  const clearSearchType = () => {
    lastSearchType.current = selectedType;
    setSelectedType(null);
    setInputText('');
    setGlobalSearchText('');
    clearSearchParams();
  };

  const handleTypeSelect = (e: React.SyntheticEvent,
    value: SearchType | string | null,
    reason: AutocompleteChangeReason
  ) => {
    if (value && typeof value === 'object' && reason === 'selectOption') {
      selectSearchType(value);
    }
  };



  const handleCloseAutoComplete = (event: any, reason: AutocompleteCloseReason) => {
    if (reason === 'escape' || reason === 'blur') {
      setShowTypeSelector(false);

      if (selectedType != null &&
        globalSearchPaths.every(value => !document.location.pathname.includes(value))) {

        clearSearchType();
      }
    }
  };

  const handleTrainingAction = (data: TrainingSearchDto) => {

    ///
    const handleCloseDialog = () => {
      setDialog(null);
    };

    ///
    const handleCloseDialogRegistration = (result: CreateEnrollResults | undefined) => {
      setDialog(null);
      if (result) {
        if (result.enrollmentId && result.enrollStatus == EnrollStatus.NotStarted) {
          resetInput();
          navigate(LmsPaths.BuildEnrollmentViewer(data.trainingType!, result.enrollmentId));
        }
      }
    };

    ///
    const handleNavigate = (pathResult: ICardDtoPathResult) => {
      resetInput();
      navigate(pathResult.url);
    };


    if (data.actions.length > 0) {
      resolveLmsTargetAction(
        data.actions[0],
        data.data,
        setDialog,
        handleCloseDialogRegistration, handleCloseDialog, handleCloseDialog, handleNavigate
      );
    }
  };

  ///
  const renderPreview = () => {
    if (searchResult == null) {
      return <Typography variant="button">
        {t("GlobalSearch.SearchPreview.Input.Placeholder")}
      </Typography>;
    }

    if (searchResult.others.length == 0
      && searchResult.users.length == 0
      && searchResult.topics.length == 0
      && searchResult.trainings.length == 0
      && searchResult.channels.length == 0
    ) {
      return (
        <Typography variant="button">
          <b>{t("GlobalSearch.SearchPreview.NoSearchResults")}</b>
        </Typography>
      );
    }

    return (
      <div style={{ maxHeight: 250, overflow: "auto" }}>
        {dialog}
        <Stack direction="column" gap={2}>
          {
            searchResult.topics.length > 0 &&
            <Grid container spacing={1}>
              {
                searchResult.topics.map((v, index) => {
                  var navUrl = KnowledgePaths.FeedTopic(v.topicId);
                  return (
                    <Grid key={index}>
                      <MprcChipChoice
                        label={v.name}
                        title={v.name}
                        onClick={navUrl ? () => goUrl(navUrl) : undefined}
                      />
                    </Grid>
                  );
                })
              }
            </Grid>
          }

          {
            searchResult.channels.length > 0 &&
            <Grid container spacing={1}>
              {
                searchResult.channels.map((v, index) => {
                  const navUrl = KnowledgePaths.FeedChannel(v.channelId);
                  return (
                    <Grid key={index} size={{ xs: 6 }}>
                      <Grid container spacing={1} justifyContent="center" alignItems="center">
                        <Grid>
                          <Avatar
                            variant="rounded"
                            sx={{ width: AppImageDimension.ThumbnailWide.width / 4, height: AppImageDimension.ThumbnailWide.height / 4 }}
                            src={v.thumbnailUrl ?? AppImages.KnowledgeChannelThumbnail}
                          />
                        </Grid>
                        <Grid size="grow">
                          <MprcClickable onClick={() => goUrl(navUrl)}>
                            <Typography variant="body2" noWrap title={v.name}>
                              {v.name}
                            </Typography>
                          </MprcClickable>
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                })
              }
            </Grid>
          }

          {
            searchResult.users.length > 0 &&
            <Grid container spacing={1}>
              {
                searchResult.users.map((v, index) => {
                  const navUrl = RootPaths.BuildUserProfile(v.userId);
                  return (
                    <Grid key={index} size={{ xs: 6 }}>
                      <MprcClickable onClick={() => goUrl(navUrl)}>
                        <Grid container spacing={1} justifyContent="center" alignItems="center">
                          <Grid>
                            <Avatar
                              src={v.thumbnailUrl ?? AppImages.UserThumbnail}
                            />
                          </Grid>
                          <Grid size="grow">
                            <Typography variant="body2">
                              {v.name}
                            </Typography>
                          </Grid>
                        </Grid>
                      </MprcClickable>
                    </Grid>
                  );
                })
              }
            </Grid>
          }

          {
            searchResult.trainings.length > 0 &&
            <List dense>
              {
                searchResult.trainings.map((v) => {
                  return (
                    <Box key={v.trainingId} sx={{ mb: 1 }}>
                      <MprcLink variant="body1" color="primary" component="a" onClick={() => handleTrainingAction(v)}>
                        <Typography variant="body1" component="p" color="primary">
                          {v.name}
                        </Typography>
                      </MprcLink>
                      <Typography variant="caption" component="p" color="muted.main">
                        {v.contentTypeName}
                      </Typography>
                    </Box>
                  );
                })
              }
            </List>
          }

          {
            searchResult.others.length > 0 &&
            <List dense>
              {
                searchResult.others.map((v) => {
                  const navUrl = v.rowType == "knowledge"
                    ? KnowledgePaths.PublicationViewerResolver(v.id, v.publicationType!, v.linkedPublicationId!) ?? ""
                    : "/"
                    ;
                  return (
                    <Box key={v.id} sx={{ mb: 1 }}>
                      <MprcClickable onClick={() => goUrl(navUrl)}>
                        <Typography variant="body2" color="primary.main">
                          {v.title}
                        </Typography>
                      </MprcClickable>
                      <Typography variant="caption" color="muted.main">
                        {v.subType}
                        {
                          v.rowType == "knowledge" &&
                          <>
                            &nbsp;&bull;&nbsp;
                            {v.authorName}
                            &nbsp;&bull;&nbsp;
                            {Formaters.relativeTime(v.lastUpdate)}
                          </>
                        }
                      </Typography>
                    </Box>
                  );
                })
              }
            </List>
          }
        </Stack>
      </div>
    );
  };

  ///
  async function load() {

    if (globalSearchText.trim() == lastSearch?.trim() || selectedType?.id == "ia") return;

    if (!keepSearchingPartialResults.current) {

      //Retoma busca parcial apenas se o início do texto for diferente do registrado quando não houve resultado (ex: limpou campo, editou parte do que já tinha digitado no início)
      if (lastSearchWithoutResults.current != null && !inputText.startsWith(lastSearchWithoutResults.current.split(" ")[0])) {
        keepSearchingPartialResults.current = true;
        lastSearchWithoutResults.current = null;
      }
      else {
        return;
      }
    }

    setOpened(true);
    setLoading(true);

    try {

      var response = await api.v1.globalSearch.globalSearchPreview({ searchText: (selectedType?.charSearch ?? "") + globalSearchText });
      setSearchResult(response.data);

      const hasResults = response.data.channels.length > 0 ||
        response.data.topics.length > 0 ||
        response.data.users.length > 0 ||
        response.data.trainings.length > 0 ||
        response.data.others.length > 0;

      if (!hasResults) {
        keepSearchingPartialResults.current = false;
        lastSearchWithoutResults.current = globalSearchText;
      }

      setLastSearch(globalSearchText);
    }
    catch (e) {
      MprcRequestSnackbarErrorReport(e);
    }
    finally {
      setLoading(false);
    }
  }

  // Verifica quais foram os termos utilizados na última Busca Global
  useEffect(() => {
    const params = new URLSearchParams(document.location.search);
    const q = params.get("q");
    if (q) {
      const st = params.get("st");
      if (st) {
        var previousSearchType = searchTypes.find(p => p.id == st);
        if (previousSearchType) selectSearchType(previousSearchType);
      }
      setInputText(q == null ? "" : decodeURIComponent(q));
    }
    else {
      clearSearchType();
    }
  }, [document.location.pathname]);

  ///
  useEffect(() => {
    if (debouncedSearchText.trim().length > MinChars) {
      keepSearchingPartialResults.current = true;
      load();
    }
  }, [debouncedSearchText]);

  return (
    <Box
      id="headerSearchOutside"
      sx={
        {
          display: "flex",
          paddingTop: "4px",
          paddingLeft: "4px",
          paddingRight: "4px",
          flexGrow: 1,
          marginLeft: "10px",
          marginRight: "10px",
          position: "relative",
          "&.searching": {
            backgroundColor: "transparent"
          }
        }
      }
    >
      <Box
        id="headerSearchInside"
        sx={
          {
            position: "relative",
            width: "100%"
          }
        }
      >
        <Autocomplete
          sx={{
            flex: 1,
            '&.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot': {
              paddingRight: 0
            }
          }}
          freeSolo
          open={true}
          options={searchTypes}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            return option.label || '';
          }}
          value={null}
          inputValue={inputText}
          onInputChange={handleInputChange}
          onChange={handleTypeSelect}
          onClose={handleCloseAutoComplete}
          filterOptions={(options) => showTypeSelector ? options : []}
          renderOption={(props, option) => (
            <Typography {...props} variant="subtitle1" component="li" sx={{ py: "0px !important", mb: option.hasSeparator ? "10px" : 0 }}>
              {option.label}
            </Typography>
          )}
          slotProps={{
            clearIndicator: {
              style: { display: "none", }
            },

          }}
          renderInput={(params) => (
            <TextField
              {...params}
              id="globalSearchInput"
              name="global-search"
              aria-label="Campo de busca global"
              fullWidth
              placeholder={getPlaceholder()}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              spellCheck={false}
              inputRef={searchRef}
              accessKey={AccessKeys.Search}
              slotProps={
                {
                  input: {
                    ...params.InputProps,
                    type: "search",
                    autoComplete: "off",
                    spellCheck: false,
                    disableUnderline: true,
                    startAdornment: selectedType != null &&
                      <Typography variant="caption" color="primary" mr={1}>{selectedType.label}&nbsp;|&nbsp;</Typography>,
                    endAdornment: (searchRef.current == null || searchRef.current.value.length == 0) &&
                      !document.location.pathname.includes(RootPaths.RagSearch) &&
                      <MprcChipChoice
                        labelPadding={1}
                        margin={0}
                        label={<><IconAI fixedWidth /> <span>Modo IA</span></>}
                        onClick={goAiMode}
                      />
                  }
                }
              }
              sx={
                {
                  backgroundColor: "#fff",
                  color: "#000",
                  border: 0,
                  borderRadius: "10px",
                  width: "100%",
                  "&:focus": {
                    outline: "none",
                  },
                  paddingLeft: "8px",
                  paddingRight: "8px",
                }
              }
            />
          )}
        />
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            id="globalSearchAutoCompleteOutside"
            square
            sx={{
              display: (opened ? "" : "none"),
              zIndex: (theme) => theme.zIndex.drawer,
              position: "absolute",
              width: "100%",
              marginTop: "2px",
              borderRadius: "10px",
            }}>
            <Box
              id="globalSearchAutoCompleteInside"
              sx={{ p: 2 }}
            >
              {
                loading
                  ? <MprcSpinnerIndicator />
                  : renderPreview()
              }
            </Box>
          </Paper>
        </ClickAwayListener>
      </Box>
    </Box>
  );
};
